import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { TextInput, Textarea, UnstyledButton } from '@mantine/core';
import type { Line } from '@nelnela/contracts';
import { fmtTime, speakerHue } from '../../lib/text';
import { draftPatch, fmtTimeMs, NUDGE_MS, parseTimeInput, type LinePatch } from '../../lib/editor';

export type TimeField = 'startMs' | 'endMs';

export interface LineEditorProps {
  line: Line;
  /** position in the editor's list (data-idx for scroll anchoring) */
  idx: number;
  /** the playing line — same fill as the read view */
  active: boolean;
  /** this row is the one being edited (frame 11b: click a line to edit) */
  expanded: boolean;
  /** ≥ 62em: time fields + row tools; below: KA/EN/speaker only (F5 mobile subset) */
  desktop: boolean;
  canMerge: boolean;
  /** locked by a job, or the learner can't edit — fields disabled with the reason */
  disabled: boolean;
  disabledReason: string | null;
  /** id of the shared <datalist> of known speakers */
  speakerListId: string;
  /** a mutation on this row is in flight */
  busy: boolean;
  /** one calm sentence under the row (a failed commit, a 422) */
  error: string | null;
  /** a transient note ("restored with a new id") */
  note: string | null;
  /** bump to drop this row's draft (banner Reload) */
  resetKey: number;
  onExpand: (lineId: string) => void;
  onCollapse: () => void;
  onSeek: (ms: number) => void;
  /** the row's draft differs from the row (mid-typing or a failed commit) */
  onDirty: (lineId: string, dirty: boolean) => void;
  /** blur / ⌘Enter → PATCH the changed fields */
  onCommit: (lineId: string, patch: LinePatch) => void;
  onNudge: (lineId: string, field: TimeField, deltaMs: number) => void;
  onSetToPlayhead: (lineId: string, field: TimeField) => void;
  onSplit: (lineId: string, atChar: number) => void;
  onMerge: (lineId: string) => void;
  onInsertAfter: (lineId: string) => void;
  onDelete: (lineId: string) => void;
}

interface Draft {
  text: string;
  translation: string;
  speaker: string;
  start: string;
  end: string;
}
const DRAFT_KEYS: (keyof Draft)[] = ['text', 'translation', 'speaker', 'start', 'end'];

function fromLine(l: Line): Draft {
  return {
    text: l.text,
    translation: l.translation ?? '',
    speaker: l.speaker,
    start: fmtTimeMs(l.startMs),
    end: fmtTimeMs(l.endMs),
  };
}

const ERROR_TINT = 'light-dark(#b0463d, #e0938c)'; // theme.other.errorTint — never the accent

/**
 * One transcript row in the editor (F5, frame 11b). Compact by default — the time gutter, the
 * speaker chip, the Georgian text and the English beneath/beside, so the eye lands where the
 * read view put it; click → the row expands into fields. Every field commits on blur or
 * ⌘/Ctrl+Enter (typing never calls the server); the row tools commit at once. Ported from the
 * local app's LineEditor (Transcript.jsx) onto per-line commits with stable ids.
 */
export const LineEditor = memo(function LineEditor({
  line,
  idx,
  active,
  expanded,
  desktop,
  canMerge,
  disabled,
  disabledReason,
  speakerListId,
  busy,
  error,
  note,
  resetKey,
  onExpand,
  onCollapse,
  onSeek,
  onDirty,
  onCommit,
  onNudge,
  onSetToPlayhead,
  onSplit,
  onMerge,
  onInsertAfter,
  onDelete,
}: LineEditorProps) {
  // ---- draft: what the fields show. Untouched fields follow the row; touched fields keep the
  // learner's text until the commit lands (then the row equals the draft) or fails (kept + error).
  const [draft, setDraft] = useState<Draft>(() => fromLine(line));
  const baseRef = useRef(line);
  const resetSeenRef = useRef(resetKey);
  const [localError, setLocalError] = useState<string | null>(null);
  useEffect(() => {
    if (baseRef.current === line && resetSeenRef.current === resetKey) return;
    const fresh = fromLine(line);
    if (resetSeenRef.current !== resetKey) {
      resetSeenRef.current = resetKey;
      setDraft(fresh);
      setLocalError(null);
    } else {
      const prev = fromLine(baseRef.current);
      setDraft((d) => {
        const n = { ...d };
        for (const k of DRAFT_KEYS) if (d[k] === prev[k]) n[k] = fresh[k];
        return n;
      });
    }
    baseRef.current = line;
  }, [line, resetKey]);

  const dirty =
    draft.text !== line.text ||
    draft.translation !== (line.translation ?? '') ||
    draft.speaker.trim() !== line.speaker;
  useEffect(() => {
    if (!dirty) return;
    onDirty(line.id, true);
    return () => onDirty(line.id, false);
  }, [dirty, line.id, onDirty]);

  const commitText = useCallback(() => {
    const patch = draftPatch(line, draft);
    if (Object.keys(patch).length) onCommit(line.id, patch);
  }, [line, draft, onCommit]);

  const commitTime = useCallback(
    (field: TimeField) => {
      const key = field === 'startMs' ? 'start' : 'end';
      const ms = parseTimeInput(draft[key]);
      if (ms === null) {
        setDraft((d) => ({ ...d, [key]: fmtTimeMs(line[field]) }));
        setLocalError('Times look like 12:34.200');
        return;
      }
      setLocalError(null);
      if (ms !== line[field]) onCommit(line.id, { [field]: ms });
      else setDraft((d) => ({ ...d, [key]: fmtTimeMs(ms) }));
    },
    [draft, line, onCommit],
  );

  const discardDraft = useCallback(() => {
    setDraft(fromLine(line));
    setLocalError(null);
  }, [line]);

  // ---- KA textarea cursor (split at cursor reads it; captured before the button's mousedown blurs)
  const kaRef = useRef<HTMLTextAreaElement>(null);
  const kaCursorRef = useRef(0);
  const captureCursor = useCallback(() => {
    const ta = kaRef.current;
    if (ta) kaCursorRef.current = ta.selectionStart ?? 0;
  }, []);

  const splitHere = useCallback(() => {
    if (disabled) return;
    commitText();
    onSplit(line.id, kaCursorRef.current);
  }, [disabled, commitText, onSplit, line.id]);

  // Opening a row puts the caret in the Georgian text (insert-after lands here too). No scroll:
  // the anchoring in TranscriptEditor already keeps the row where the learner clicked.
  useEffect(() => {
    if (expanded && !disabled) kaRef.current?.focus({ preventScroll: true });
  }, [expanded, disabled]);

  // ---- delete: 2-tap confirm, disarms after 3 s
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);

  const onFieldKey = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape') {
      e.currentTarget.blur();
      return;
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      e.currentTarget.blur(); // blur commits
    }
  };

  const hue = line.speaker ? speakerHue(line.speaker) : null;
  const dot = hue !== null && (
    <span
      aria-hidden
      style={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        flexShrink: 0,
        background: `light-dark(oklch(0.55 0.09 ${hue}), oklch(0.72 0.08 ${hue}))`,
      }}
    />
  );
  const shownError = error ?? localError;

  // ------------------------------------------------------------ compact row
  if (!expanded) {
    const gutter = (
      <UnstyledButton
        onClick={(e) => {
          e.stopPropagation();
          onSeek(line.startMs);
        }}
        title="jump here"
        aria-label={`Jump to ${fmtTime(line.startMs)}`}
        style={{
          display: 'flex',
          flexDirection: desktop ? 'column' : 'row',
          alignItems: desktop ? 'flex-start' : 'center',
          gap: desktop ? 2 : 6,
          width: desktop ? 118 : undefined,
          flexShrink: 0,
          minHeight: 22,
          fontSize: 10.5,
          color: 'var(--nn-faint)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span>
          {fmtTime(line.startMs)} – {fmtTime(line.endMs)}
        </span>
        {line.speaker && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--nn-muted)' }}>
            {dot}
            <span style={{ fontVariantNumeric: 'normal' }}>{line.speaker}</span>
          </span>
        )}
      </UnstyledButton>
    );
    const en = line.translation ? (
      <span style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--nn-muted)' }}>
        {line.translation}
      </span>
    ) : (
      <span style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--nn-faint)' }}>
        translation pending
      </span>
    );
    return (
      <div
        role="listitem"
        data-idx={idx}
        data-active={active ? 'true' : undefined}
        className={`nn-line${active ? ' nn-line--playing' : ''}`}
        tabIndex={0}
        onClick={() => onExpand(line.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.target === e.currentTarget) {
            e.preventDefault();
            onExpand(line.id);
          }
        }}
        onContextMenu={(e) => e.preventDefault()}
        style={{
          display: 'flex',
          flexDirection: desktop ? 'row' : 'column',
          alignItems: desktop ? 'flex-start' : 'stretch',
          gap: desktop ? 12 : 3,
          padding: desktop ? '8px 18px' : '8px 14px',
          borderBottom: '1px solid var(--nn-hairline)',
          borderLeft: '2px solid transparent',
          cursor: 'text',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent',
          minHeight: 40,
          contentVisibility: 'auto',
          containIntrinsicSize: 'auto 3.2rem',
        }}
      >
        {gutter}
        <div
          lang="ka"
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 15.5,
            lineHeight: 1.5,
            color: active ? 'var(--nn-text-strong)' : 'var(--nn-text)',
          }}
        >
          {line.text}
          {dirty && (
            <span style={{ marginLeft: 8, fontSize: 10.5, color: ERROR_TINT }}>unsaved edit</span>
          )}
          {!dirty && (shownError || note) && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 10.5,
                color: shownError ? ERROR_TINT : 'var(--nn-muted)',
              }}
            >
              {shownError ?? note}
            </span>
          )}
        </div>
        <div style={{ width: desktop ? '32%' : undefined, maxWidth: desktop ? 260 : undefined }}>
          {en}
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------ expanded row
  const disabledTitle = disabledReason ?? undefined;
  const speakerInput = (
    <TextInput
      size="xs"
      list={speakerListId}
      placeholder="speaker"
      aria-label="Speaker"
      value={draft.speaker}
      disabled={disabled}
      title={disabledTitle}
      onChange={(e) => setDraft((d) => ({ ...d, speaker: e.currentTarget.value }))}
      onBlur={commitText}
      onKeyDown={onFieldKey}
      style={{ width: desktop ? 160 : '100%' }}
      styles={{ input: { minHeight: desktop ? 30 : 40, userSelect: 'text' } }}
    />
  );
  const kaInput = (
    <Textarea
      ref={kaRef}
      lang="ka"
      autosize
      minRows={1}
      spellCheck={false}
      aria-label="Georgian text"
      value={draft.text}
      disabled={disabled}
      title={disabledTitle}
      onChange={(e) => setDraft((d) => ({ ...d, text: e.currentTarget.value }))}
      onBlur={() => {
        captureCursor();
        commitText();
      }}
      onSelect={captureCursor}
      onKeyUp={captureCursor}
      onClick={captureCursor}
      onKeyDown={(e) => {
        // ⏎ splits at the cursor (frame 11b) — at either end of the text it just commits;
        // ⇧⏎ keeps a newline; ⌘⏎ commits; Esc blurs.
        if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey && desktop) {
          e.preventDefault();
          captureCursor();
          const at = kaCursorRef.current;
          if (at <= 0 || at >= draft.text.length) e.currentTarget.blur();
          else splitHere();
          return;
        }
        onFieldKey(e);
      }}
      styles={{
        input: {
          fontSize: 15.5,
          lineHeight: 1.5,
          fontFamily: 'inherit',
          userSelect: 'text',
          // no `minHeight` here: react-textarea-autosize (Mantine `autosize`) throws on it —
          // `minRows` + padding give the height
          padding: '8px 12px',
        },
      }}
    />
  );
  const enInput = (
    <Textarea
      autosize
      minRows={1}
      placeholder="English translation"
      aria-label="English translation"
      value={draft.translation}
      disabled={disabled}
      title={disabledTitle}
      onChange={(e) => setDraft((d) => ({ ...d, translation: e.currentTarget.value }))}
      onBlur={commitText}
      onKeyDown={onFieldKey}
      styles={{
        input: {
          fontSize: 12.5,
          lineHeight: 1.5,
          color: 'var(--nn-muted)',
          userSelect: 'text',
          padding: '8px 12px',
        },
      }}
    />
  );
  const status = (shownError || note || (dirty && !busy)) && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11.5, minHeight: 20 }}>
      {shownError ? (
        <>
          <span style={{ color: ERROR_TINT }}>{shownError}</span>
          {dirty && (
            <UnstyledButton
              onClick={discardDraft}
              style={{ fontSize: 11.5, color: 'var(--nn-link)' }}
            >
              discard edit
            </UnstyledButton>
          )}
        </>
      ) : note ? (
        <span style={{ color: 'var(--nn-muted)' }}>{note}</span>
      ) : (
        <span style={{ color: 'var(--nn-faint)' }}>unsaved — ⌘⏎ or click away to save</span>
      )}
    </div>
  );

  if (!desktop) {
    // Mobile subset (F5): fix the text / translation / speaker of one line.
    return (
      <div
        role="listitem"
        data-idx={idx}
        data-active={active ? 'true' : undefined}
        className={`nn-line${active ? ' nn-line--playing' : ''}`}
        style={{
          padding: '10px 14px 12px 12px',
          borderLeft: '2px solid var(--nn-accent)',
          borderBottom: '1px solid var(--nn-hairline)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 10.5,
            color: 'var(--nn-faint)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span>
            {fmtTime(line.startMs)} – {fmtTime(line.endMs)}
          </span>
          <span style={{ flex: 1 }} />
          <ToolButton onClick={() => onSeek(line.startMs)} title="jump here">
            ▶ jump
          </ToolButton>
          <ToolButton onClick={onCollapse} title="close this row">
            close
          </ToolButton>
        </div>
        {speakerInput}
        {kaInput}
        {enInput}
        {disabled && disabledReason && (
          <div style={{ fontSize: 11.5, color: 'var(--nn-muted)' }}>{disabledReason}</div>
        )}
        {status}
      </div>
    );
  }

  return (
    <div
      role="listitem"
      data-idx={idx}
      data-active={active ? 'true' : undefined}
      className={`nn-line${active ? ' nn-line--playing' : ''}`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '10px 18px 10px 16px',
        borderLeft: '2px solid var(--nn-accent)',
        borderBottom: '1px solid var(--nn-hairline)',
        background: active ? undefined : 'var(--nn-panel)',
      }}
    >
      {/* time column: start / end fields with ⏺ (playhead) and ±100 ms; ▶ jump */}
      <div style={{ width: 176, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <TimeRow
          label="start"
          value={draft.start}
          disabled={disabled}
          disabledReason={disabledReason}
          onChange={(v) => setDraft((d) => ({ ...d, start: v }))}
          onBlur={() => commitTime('startMs')}
          onKeyDown={onFieldKey}
          onPlayhead={() => onSetToPlayhead(line.id, 'startMs')}
          onNudge={(delta) => onNudge(line.id, 'startMs', delta)}
        />
        <TimeRow
          label="end"
          value={draft.end}
          disabled={disabled}
          disabledReason={disabledReason}
          onChange={(v) => setDraft((d) => ({ ...d, end: v }))}
          onBlur={() => commitTime('endMs')}
          onKeyDown={onFieldKey}
          onPlayhead={() => onSetToPlayhead(line.id, 'endMs')}
          onNudge={(delta) => onNudge(line.id, 'endMs', delta)}
        />
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <ToolButton onClick={() => onSeek(line.startMs)} title="seek the player to this line">
            ▶ jump
          </ToolButton>
          {busy && <span style={{ fontSize: 10.5, color: 'var(--nn-faint)' }}>saving…</span>}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {dot}
          {speakerInput}
          <span style={{ flex: 1 }} />
          {disabled && disabledReason && (
            <span style={{ fontSize: 11.5, color: 'var(--nn-muted)' }}>{disabledReason}</span>
          )}
        </div>
        {kaInput}
        {enInput}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <ToolButton
            disabled={disabled}
            title={disabledTitle ?? 'split this line at the cursor in the Georgian text (⏎)'}
            onClick={splitHere}
          >
            ✂ split
          </ToolButton>
          <ToolButton
            disabled={disabled || !canMerge}
            title={
              disabledTitle ?? (canMerge ? 'merge with the next line' : 'this is the last line')
            }
            onClick={() => onMerge(line.id)}
          >
            ⤵ merge next
          </ToolButton>
          <ToolButton
            disabled={disabled}
            title={disabledTitle ?? 'insert a new line after this one'}
            onClick={() => onInsertAfter(line.id)}
          >
            ＋ insert after
          </ToolButton>
          <ToolButton
            disabled={disabled}
            title={disabledTitle ?? (armed ? 'tap again to delete' : 'delete this line')}
            danger
            onClick={() => {
              if (!armed) {
                setArmed(true);
                return;
              }
              setArmed(false);
              onDelete(line.id);
            }}
          >
            {armed ? '✕ delete? (tap again)' : '✕ delete'}
          </ToolButton>
          <span style={{ flex: 1 }} />
          <ToolButton onClick={onCollapse} title="close this row (Esc in a field also blurs)">
            close
          </ToolButton>
        </div>
        {status}
      </div>
    </div>
  );
});

function TimeRow({
  label,
  value,
  disabled,
  disabledReason,
  onChange,
  onBlur,
  onKeyDown,
  onPlayhead,
  onNudge,
}: {
  label: string;
  value: string;
  disabled: boolean;
  disabledReason: string | null;
  onChange: (v: string) => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  onPlayhead: () => void;
  onNudge: (deltaMs: number) => void;
}) {
  const title = disabledReason ?? undefined;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <TextInput
        size="xs"
        aria-label={label}
        value={value}
        disabled={disabled}
        title={title}
        onChange={(e) => onChange(e.currentTarget.value)}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
            return;
          }
          onKeyDown(e);
        }}
        style={{ width: 84 }}
        styles={{
          input: {
            fontVariantNumeric: 'tabular-nums',
            fontSize: 11,
            textAlign: 'center',
            paddingInline: 4,
            userSelect: 'text',
          },
        }}
      />
      <ToolButton
        disabled={disabled}
        title={title ?? `set ${label} to the playhead`}
        onClick={onPlayhead}
        narrow
      >
        ⏺
      </ToolButton>
      <ToolButton
        disabled={disabled}
        title={title ?? `${label} −${NUDGE_MS} ms`}
        onClick={() => onNudge(-NUDGE_MS)}
        narrow
      >
        −.1
      </ToolButton>
      <ToolButton
        disabled={disabled}
        title={title ?? `${label} +${NUDGE_MS} ms`}
        onClick={() => onNudge(NUDGE_MS)}
        narrow
      >
        +.1
      </ToolButton>
    </div>
  );
}

function ToolButton({
  children,
  onClick,
  disabled,
  title,
  danger,
  narrow,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  danger?: boolean;
  narrow?: boolean;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={typeof children === 'string' ? children : undefined}
      style={{
        minHeight: 32,
        minWidth: narrow ? 28 : undefined,
        padding: narrow ? '0 3px' : '0 8px',
        borderRadius: 5,
        fontSize: 11.5,
        whiteSpace: 'nowrap',
        color: danger ? ERROR_TINT : 'var(--nn-muted)',
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {children}
    </UnstyledButton>
  );
}
